/**
 * useTopBar -- breadcrumb derivation + autosave indicator state.
 *
 * Extracted from the now-deleted TheTopBar.vue during route-fork PR-R2b.
 * Each role-specific layout shell (DistributorLayout, SuperAdminLayout)
 * renders its own breadcrumb / save-indicator chrome by composing this
 * helper with `useStore()` and `useRoute()` -- the layouts decide which
 * elements to render and where, this helper just shapes the data.
 *
 * Returned shape:
 *   {
 *     crumbs: ComputedRef<Array<{ label, to }>>,
 *     showSaveIndicator: ComputedRef<boolean>,
 *     saveState: ComputedRef<{ label, dotColor, title }>,
 *   }
 *
 * `crumbs[last].to === null` marks the current (non-clickable) breadcrumb.
 * `saveState.label` is the user-visible string. `saveState.dotColor` is the
 * indicator-dot CSS color. `saveState.title` populates the tooltip.
 *
 * Behaviour notes:
 *   - Owner / Builder routes now live under /distributor/* (route-fork
 *     PR-R2b). The breadcrumb walker accepts both the new /distributor/*
 *     paths and the legacy /admin/* paths for the redirect-compatibility
 *     window.
 *   - The save indicator only shows on store-edit routes
 *     (/distributor/stores/:id/edit[/:section]).
 *   - Save state is read from `state.storeEntity.autoSaveState`. States:
 *       'saved'  -> green dot, "All changes saved"
 *       'saving' -> yellow dot, "Saving..."
 *       'dirty'  -> yellow dot, "Unsaved changes"
 *       'error'  -> red dot, "Save failed"
 */
import { computed } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

const SAVE_PRESETS = {
    saving: { label: "Saving...", dotColor: "#FDD835", title: "Saving in progress" },
    dirty: { label: "Unsaved changes", dotColor: "#FDD835", title: "Saving shortly..." },
    error: {
        label: "Save failed",
        dotColor: "#ef0405",
        title: "The last autosave failed. Edits may not be persisted.",
    },
    saved: { label: "All changes saved", dotColor: "#00d084", title: "All changes saved" },
};

// Design-parity PR-1: store editor URL is /distributor/stores/:id/edit
// (with optional /:section). The legacy /campaigns/ pattern was removed
// 2026-10-21. One truth source for the crumb + save-indicator code paths.
const STORE_EDIT_RE = /^\/distributor\/stores\/[^/]+\/edit(\/|$)/;
function _isStoreEditPath(path) {
    if (!path) return false;
    return STORE_EDIT_RE.test(path);
}
function _isEditorPath(path) {
    if (!path) return false;
    return _isStoreEditPath(path);
}

function buildCrumbs(path, editedName) {
    const list = [];
    if (!path) return list;

    // Accept both the new /distributor/* and legacy /admin/* prefixes during
    // the redirect-compatibility window.
    const isDistributor = path.startsWith("/distributor") || path.startsWith("/admin");
    if (!isDistributor) return list;

    const baseTo = path.startsWith("/distributor") ? "/distributor" : "/admin";
    list.push({ label: "Stores", to: baseTo });

    if (_isEditorPath(path) || editedName) {
        list.push({ label: editedName || "Editing store", to: null });
    } else if (path.includes("/products")) {
        list.push({ label: "Products", to: null });
    } else if (path.includes("/distributors")) {
        list.push({ label: "Distributors", to: null });
    } else if (path.includes("/accounts")) {
        list.push({ label: "User Accounts", to: null });
    } else if (path.includes("/help")) {
        // Replaces the "/tutorials" -> "Video Tutorials" and "/guides" ->
        // "Guides & Docs" crumbs, removed with the legacy help teardown
        // (`docs/user/HELP-SYSTEM-DESIGN.md` decision 5).
        list.push({ label: "Help", to: null });
    } else if (path.includes("/profile")) {
        list.push({ label: "My Profile", to: null });
    }
    return list;
}

export function useTopBar() {
    const route = useRoute();
    const store = useStore();

    const crumbs = computed(() => {
        const path = route?.path || "";
        const editedName = store?.getters?.getEditedCampaignName || "";
        return buildCrumbs(path, editedName);
    });

    const showSaveIndicator = computed(() => {
        const path = route?.path || "";
        return _isEditorPath(path);
    });

    const saveState = computed(() => {
        const s = store?.state?.storeEntity?.autoSaveState || "saved";
        return SAVE_PRESETS[s] || SAVE_PRESETS.saved;
    });

    return { crumbs, showSaveIndicator, saveState };
}

// Pure helpers exported for direct unit testing. The composable wires these
// up against Vue's reactive `useRoute()` / `useStore()`; the helpers run
// against plain values so tests don't need a router/store harness.
export const _internal = {
    buildCrumbs,
    SAVE_PRESETS,
};

// Named export for direct import in tests + downstream helpers that want
// the editor-path predicate without going through the composable.
export { _isStoreEditPath, _isEditorPath };

export default useTopBar;
